import {
  BROADCAST_IMAGE,
  CHANNEL_FILE,
  uploadFile,
  uploadImage,
} from 'actions/FileUploadActionCreators';
import moment from 'moment';

export const MessageType = {
  TEXT: 1,
  IMAGE: 2,
  FORM: 3,
  PDF: 5,
};

export const MessageCategory = {
  GROUP_CHAT_MESSAGE: 'chat_group_message',
  P2P_MESSAGE: 'message',
  DRAFT: 'draft',
  CHANNEL_MESSAGE: 'channel_message',
  CHANNEL_BROADCAST_MESSAGE: 'channel_broadcast_message',
  CHANNEL_DRAFT: 'channel_draft',
};

export const formatMessage = ({ type = 1, message, token }) =>
  new Promise((resolve, reject) => {
    switch (type) {
      case MessageType.FORM:
        return resolve({
          type: MessageType.FORM,
          body: message,
          // '[  # THIS IS A STRING
          //  {
          //     "key": "Title",
          //     "value": "Sales Confirmation for Product X",
          //     "owner": "Sagar",
          //     "company": "sourcesage.co",
          //     "created_at": 12719827389,
          //     "_flag": false,
          //     "h": false
          //   },
          //   {
          //     "key": "giá",
          //     "value": "$1200",
          //     "_flag": false,
          //     "h": false
          //   }
          // ]'
        });

      case MessageType.PDF:
        // '{  # THIS IS A STRING
        //   "name": "1231231-123123-dfs-df-3423.pdf",
        //   "url": "files/message/2",
        //   "bytes": 12391082
        // }'
        if (typeof message.url === 'string') {
          return resolve({
            type: MessageType.PDF,
            body: JSON.stringify(message),
          });
        }
        return uploadFile({
          fileData: message,
          type: CHANNEL_FILE,
          token,
        })
          .then(url =>
            resolve({
              type: MessageType.PDF,
              body: JSON.stringify({
                name: message.name,
                url,
                bytes: message.size,
              }),
            })
          )
          .catch(e => reject(e));

      case MessageType.IMAGE:
        if (typeof message === 'string') {
          return resolve({
            type: MessageType.IMAGE,
            // extract the imageKey only
            body: message
              .replace(/^"(.*)"$/, '$1') // $1 means first capturing group
              .replace(
                /^(https:\/\/s3-ap-southeast-1\.amazonaws\.com\/sourcesage\/)(.*)$/,
                '$2'
              ),
          });
        }
        return uploadImage({
          imageData: message,
          type: BROADCAST_IMAGE,
          token,
        }).then(imageKey =>
          resolve({
            type: MessageType.IMAGE,
            body: imageKey,
          })
        );

      case MessageType.TEXT:
      default:
        return resolve({
          type: MessageType.TEXT,
          body: message,
        });
    }
  });

class Message {
  constructor({
    id,
    type,
    category,
    body,
    timestamp,
    from,
    to,
    direction,
    groupId = -1,
  }) {
    this.id = id;
    this.type = type;
    this.category = category;
    this.body = body;
    this.date = timestamp;
    this.from = from;
    this.to = to;

    if (category === MessageCategory.CHANNEL_MESSAGE) {
      this.sentToChannel = direction === 1 || direction;
    }
    if (category === MessageCategory.GROUP_CHAT_MESSAGE) {
      this.groupId = groupId;
    }
  }

  setBody(json) {
    this.body = json;
  }

  setFrom(from) {
    this.from = from;
  }

  setTo(to) {
    this.to = to;
  }

  getId() {
    return this.id;
  }

  getMessageType() {
    return this.type;
  }

  getMessageCategory() {
    return this.category;
  }

  getDisplayBody() {
    return this.body;
  }

  getDisplayDate(format = 'lll') {
    // return this.date.locale(moment.locale()).format(format);
    return moment.unix(this.date).format(format);
  }

  getPreviewDate() {
    if (moment().startOf('day') < moment.unix(this.date)) {
      return moment.unix(this.date).format('LT');
    }
    return moment.unix(this.date).format('ddd');
  }

  getDisplayTime() {
    // return this.date.locale(moment.locale()).format('LT');
    return moment.unix(this.date).format('LT');
  }

  getTitle() {
    if (this.getMessageType() !== MessageType.FORM) return null;
    const form = this.getDisplayBody();
    if (!form || !form.length) return null;
    const keyValueMap = Object.assign(
      {},
      ...form.map(each => ({ [each.key]: each.value }))
    );
    /* eslint dot-notation: 0 */
    if (keyValueMap['Title']) return keyValueMap['Title'];
    if (keyValueMap['称号']) return keyValueMap['称号'];
    if (keyValueMap['タイトル']) return keyValueMap['タイトル'];
    return form[0].value;
  }

  getCreatorName() {
    if (this.getMessageType() !== MessageType.FORM) return null;
    const form = this.getDisplayBody();
    return form[0].owner || '';
  }

  getCreatorCompany() {
    if (this.getMessageType() !== MessageType.FORM) return null;
    const form = this.getDisplayBody();
    return form[0].company || '';
  }
}

const modifyMessageBodyByMessageType = message => {
  if (message.type === MessageType.FORM) {
    message.setBody(JSON.parse(message.body));
  }

  if (message.type === MessageType.PDF) {
    const body = JSON.parse(message.body);
    if (body.url.startsWith('http')) {
      message.setBody(body);
    } else {
      message.setBody({
        ...body,
        url: `https://s3-ap-southeast-1.amazonaws.com/sourcesage/${body.url}`,
      });
    }
  }

  if (message.type === MessageType.IMAGE) {
    if (!message.body.startsWith('http')) {
      message.setBody(
        `https://s3-ap-southeast-1.amazonaws.com/sourcesage/${message.body}`
      );
    }
  }

  return message;
};

export const convertChatJsonToMessage = json => {
  // INPUT: in case json supplied is null, a.k.a. follower with no messages
  // set category to CHANNEL_MESSAGE, and everything else to null
  // OUTPUT: container shows user but no previews and no messages
  if (!json) {
    return new Message({
      id: null,
      type: null,
      category: MessageCategory.CHANNEL_MESSAGE,
      body: '',
      timestamp: null,
      from: null,
      to: null,
      direction: null, // Message.sentToChannel will be null, not true or false
      groupId: -1,
    });
  }

  const message = new Message({
    id: json.id,
    type: json.body.MessageType,
    category: MessageCategory.CHANNEL_MESSAGE,
    body: json.body.Body,
    timestamp: json.timestamp_in_seconds || json.timestamp,
    from: json.direction_to_channel
      ? json.follower_id
      : json.company_channel_id,
    to: json.direction_to_channel ? json.company_channel_id : json.follower_id,
    direction: json.direction_to_channel,
    groupId: -1,
  });

  return modifyMessageBodyByMessageType(message);
};

export const convertOCFolderItemToMessage = json => {
  // { id, type, category, body, timestamp, from, to, direction, channelId, groupId=-1 }
  const message = new Message({
    id: json.id,
    type: json.type,
    category: json.table_name,
    body: json.body,
    timestamp: json.timestamp_in_seconds,
    from: { name: json.from_name, avatar: json.from_avatar },
    to: { name: json.to_name, avatar: json.to_avatar },
    direction: json.direction_to_channel,
  });
  return modifyMessageBodyByMessageType(message);
};

export const convertFolderItemToMessage = json => {
  // { id, type, category, body, timestamp, from, to, direction, channelId, groupId }
  const message = new Message({
    id: json.id,
    type: json.type,
    category: json.table_name,
    body: json.body,
    timestamp: json.timestamp_in_seconds,
    from: {
      id: parseInt(json.from_id, 10),
      avatar: json.from_avatar,
      name: json.from_name,
    },
    to: {
      id: parseInt(json.from_id, 10),
      avatar: json.to_avatar,
      name: json.to_name,
    },
    direction: json.direction_to_channel,
    groupId: parseInt(json.group_id, 10),
  });

  return modifyMessageBodyByMessageType(message);
};

export default Message;



// WEBPACK FOOTER //
// ./app/utils/Message/index.js